import { useEffect, useMemo, useState } from 'react';
import { IconButton, Grid, Button, Tooltip } from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import EditIcon from '@mui/icons-material/Edit';
import {
  CellClassParams,
  ColDef,
  ICellRendererParams,
  ValueFormatterParams,
} from 'ag-grid-community';
import { useAppSelector } from '../../store/hooks';
import { OrderListingResponse, OrderFilters, OrderStatus, UserRole } from '../../types';
import { useGetAllOrdersQuery, usePrefetch } from './orders-api-slice';
import { useInteractiveGrid } from '../../hooks';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DataGridPage } from '../../components/data-grid';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { selectCurrentUser } from '../user';
import {
  selectManageOrders,
  selectAccessOrders,
  selectManageManufacturers,
  selectManageDealers,
  selectAccessDealers,
} from '../user';
import { DateUtils, getOrderStatusColor, getOrderStatusLabel } from '../../utils';
import { getCreateOrderQueryAction } from './create-order-from-query';
import NewOrderWizard from './new-order-wizard';
import { FilterDateField, FilterTextField } from '../../components/filters';
import FilterSelectField from '../../components/filters/filter-select-field.tsx';

const OrderActionsCell = (params: ICellRendererParams) => {
  const navigate = useNavigate();
  const canManageOrders = useAppSelector(selectManageOrders);
  const canAccessOrders = useAppSelector(selectAccessOrders);
  const currentUser = useAppSelector(selectCurrentUser);

  const canAccessTraveler =
    currentUser?.role === UserRole.ADMINISTRATOR ||
    currentUser?.role === UserRole.SUPERVISOR ||
    currentUser?.role === UserRole.CSR;

  return (
    <>
      <Tooltip title={!canManageOrders ? 'View Order' : 'Edit Order'}>
        <IconButton
          size="small"
          onClick={() => navigate(`/order-details/${params.data.id}`)}
          aria-label={!canManageOrders ? 'view order' : 'edit order'}
        >
          {!canManageOrders && canAccessOrders ? (
            <VisibilityIcon fontSize="small" />
          ) : (
            <EditIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
      {canAccessTraveler && (
        <Tooltip title="Work Order Traveler">
          <IconButton
            size="small"
            onClick={() => navigate(`/order-details/${params.data.id}/traveler`)}
            aria-label="work order traveler"
            sx={{ color: 'primary.main' }}
          >
            <AssignmentIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </>
  );
};

export const OrderManagementPage = () => {
  const hasAccess = useAppSelector(selectAccessOrders);
  const prefetchOrders = usePrefetch('getOrders');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const canManageOrders = useAppSelector(selectManageOrders);
  const canManageManufacturers = useAppSelector(selectManageManufacturers);
  const canManageDealers = useAppSelector(selectManageDealers);
  const canAccessDealers = useAppSelector(selectAccessDealers);
  const [showNewOrderWizard, setShowNewOrderWizard] = useState(false);

  const {
    gridContainerRef,
    pagination,
    filters,
    filtersExpanded,
    setFiltersExpanded,
    response: orderListingResponse,
    error,
    handlePageChange,
    handlePageSizeChange,
    handleFilterChange,
    onSortChanged,
    handleClearFilters,
    handleNextPageHover,
  } = useInteractiveGrid<OrderFilters, OrderListingResponse>({
    hasAccess,
    prefetchFunction: prefetchOrders,
    getDataQuery: useGetAllOrdersQuery,
    debounceOptions: {
      enabled: true,
      delay: 300,
    },
    spinnerOptions: {
      showSpinnerOnSort: true,
      showSpinnerOnFilter: true,
      showSpinnerOnPageChange: true,
    },
  });

  const handleOpenNewOrderWizard = () => {
    setShowNewOrderWizard(true);
  };

  const handleCloseNewOrderWizard = () => {
    setShowNewOrderWizard(false);
  };

  useEffect(() => {
    const { hasCreateParam, openWizard } = getCreateOrderQueryAction(
      searchParams.get('create'),
      canManageOrders
    );

    if (!hasCreateParam) {
      return;
    }

    if (openWizard) {
      setShowNewOrderWizard(true);
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('create');
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, canManageOrders, setSearchParams]);

  // Column definitions for AG Grid
  const columnDefs = useMemo<ColDef[]>(() => {
    const columns: ColDef[] = [];

    // Only add manufacturer column for users who can manage manufacturers
    if (canManageManufacturers) {
      columns.push({
        field: 'licenseeName',
        headerName: 'Manufacturer',
        sortable: true,
        width: 120,
      });
    }

    // Only add dealer/customer column for users who can access dealers
    if (canAccessDealers) {
      columns.push({
        field: 'dealerName',
        headerName: 'Customer Name',
        sortable: true,
        flex: 1.5,
      });
    }

    columns.push(
      {
        field: 'orderNumber',
        headerName: 'Order #',
        sortable: true,
        flex: 1.5,
      },
      {
        field: 'po',
        headerName: 'PO #',
        sortable: true,
        width: 120,
      },
      {
        field: 'jobName',
        headerName: 'Job Name',
        sortable: true,
        flex: 1.5,
      },
      {
        field: 'orderDate',
        headerName: 'Order Date',
        sortable: true,
        width: 120,
        valueFormatter: (params: ValueFormatterParams) =>
          DateUtils.toDisplayDate(params.data?.orderDate),
      },
      {
        field: 'dueDate',
        headerName: 'Due Date',
        sortable: true,
        width: 120,
        valueFormatter: (params: ValueFormatterParams) =>
          DateUtils.toDisplayDate(params.data?.dueDate),
      },
      {
        field: 'status',
        headerName: 'Status',
        sortable: true,
        width: 130,
        valueFormatter: (params: ValueFormatterParams) => getOrderStatusLabel(params.data?.status),
        cellStyle: (params: CellClassParams<OrderListingResponse, string>) => {
          const color = getOrderStatusColor(params.data?.status);
          return { color };
        },
      },
      {
        headerName: 'Actions',
        width: 110,
        sortable: false,
        cellRenderer: OrderActionsCell,
      }
    );

    return columns;
  }, [canManageManufacturers, canAccessDealers]);

  const statusOptions = useMemo(() => {
    return Object.keys(OrderStatus)
      .filter(key => !isNaN(Number(key)))
      .map(key => {
        const status = Number(key) as OrderStatus;
        return {
          value: status.toString(),
          label: getOrderStatusLabel(status),
        };
      });
  }, []);

  if (!hasAccess) {
    return null;
  }

  const renderFilterControls = () => {
    return (
      <Grid container spacing={1}>
        {/* Only show manufacturer filter for users who can manage manufacturers */}
        {canManageManufacturers && (
          <Grid item xs={12} sm={6} md={2}>
            <FilterTextField
              label="Manufacturer"
              value={filters.licenseeName ?? ''}
              onChange={value => handleFilterChange('licenseeName', value)}
              hasValue={!!filters.licenseeName}
            />
          </Grid>
        )}

        {/* Only show customer name filter for users who can manage dealers */}
        {canManageDealers && (
          <Grid item xs={12} sm={6} md={2}>
            <FilterTextField
              label="Customer Name"
              value={filters.dealer ?? ''}
              onChange={value => handleFilterChange('dealer', value)}
              hasValue={!!filters.dealer}
            />
          </Grid>
        )}

        <Grid item xs={12} sm={6} md={1.5}>
          <FilterTextField
            label="Order #"
            value={filters.orderNumber ?? ''}
            onChange={value => handleFilterChange('orderNumber', value)}
            hasValue={!!filters.orderNumber}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={1.5}>
          <FilterTextField
            label="PO #"
            value={filters.po ?? ''}
            onChange={value => handleFilterChange('po', value)}
            hasValue={!!filters.po}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <FilterTextField
            label="Job Name"
            value={filters.jobName ?? ''}
            onChange={value => handleFilterChange('jobName', value)}
            hasValue={!!filters.jobName}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={1.5}>
          <FilterSelectField
            label="Status"
            value={filters.status ?? ''}
            onChange={value => handleFilterChange('status', value)}
            hasValue={!!filters.status}
            options={statusOptions}
            allOptionLabel="All Status"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <FilterDateField
            label="Order Date"
            value={filters.orderDate ?? ''}
            onChange={value => handleFilterChange('orderDate', value)}
            hasValue={!!filters.orderDate}
            allowToggle={true}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={1.5} sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            variant="outlined"
            onClick={handleClearFilters}
            fullWidth
            size="small"
            sx={{ fontSize: '0.75rem' }}
          >
            Clear All
          </Button>
        </Grid>
      </Grid>
    );
  };

  return (
    <>
      <DataGridPage<OrderListingResponse, OrderFilters>
        title="Order Management"
        addButtonText="Create Order"
        addButtonPath="#"
        columnDefs={columnDefs}
        response={orderListingResponse}
        error={error}
        pagination={pagination}
        filters={filters}
        filtersExpanded={filtersExpanded}
        gridContainerRef={gridContainerRef}
        handlePageChange={handlePageChange}
        handlePageSizeChange={handlePageSizeChange}
        handleSortChanged={onSortChanged}
        handleNextPageHover={handleNextPageHover}
        setFiltersExpanded={setFiltersExpanded}
        renderFilterControls={renderFilterControls}
        navigate={path => {
          if (path === '#') {
            handleOpenNewOrderWizard();
          } else {
            navigate(path);
          }
        }}
        hideAddButton={!canManageOrders}
      />

      {showNewOrderWizard && <NewOrderWizard onClose={handleCloseNewOrderWizard} />}
    </>
  );
};

export default OrderManagementPage;
